import './styles.css';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Calendario from './pages/Calendario';
import PanelInicio from './pages/PanelInicio/PanelInicio';
import TarjetaPaciente from './pages/TarjetaPaciente';
import FichaPaciente from './pages/FichaPaciente';
import ModalGestionUsuario from './components/ModalGestionUsuario';
import ModalAdminUsuarios from './components/ModalAdminUsuarios';
import ModalConfirmacion from './components/ModalConfirmacion';
import ModalNuevoPaciente from './components/ModalNuevoPaciente';
import ModalBandejaGoogle from './components/ModalBandejaGoogle';
import InstagramLink from './components/InstagramLink';
import TrialBanner from './components/TrialBanner';
import { getPatients, createPatient, deletePatient, logout, getPendingEventsCount } from "./api/api";
import React, { useState, useEffect } from 'react';

function calcularEdad(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const mes = hoy.getMonth() - nacimiento.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
  return edad >= 0 ? edad : null;
}

function formatearPaciente(p) {
  const nombreStr = `${p.last_name || ''} ${p.first_name || ''}`.trim();
  const edad = calcularEdad(p.birth_date);

  const camposBase = [
    { id: 'birth_date', field_id: 'birth_date', label: 'Fecha de nacimiento', value: p.birth_date || '', eliminable: false, choices: null, type: 'DateField' },
    { id: 'sex', field_id: 'sex', label: 'Sexo', value: p.sex || '', eliminable: false, choices: [
      { value: 'M', label: 'Masculino' },
      { value: 'F', label: 'Femenino' },
      { value: 'O', label: 'Otro' }
    ], type: 'CharField' },
    { id: 'address', field_id: 'address', label: 'Dirección', value: p.address || '', eliminable: false, choices: null, type: 'CharField' },
    { id: 'maps_link', field_id: 'maps_link', label: 'Link Google Maps', value: p.maps_link || '', eliminable: false, choices: null, type: 'URLField' },
    { id: 'city', field_id: 'city', label: 'Ciudad', value: p.city || '', eliminable: false, choices: null, type: 'CharField' },
    { id: 'consultation_reason', field_id: 'consultation_reason', label: 'Motivo de consulta', value: p.consultation_reason || '', eliminable: false, choices: null, type: 'TextField' },
    { id: 'pathological_background', field_id: 'pathological_background', label: 'Antecedentes patológicos', value: p.pathological_background || '', eliminable: false, choices: null, type: 'TextField' },
  ];

  const camposCustom = Object.entries(p.custom_fields_data || {})
    .map(([label, value]) => ({
      id: label,
      field_id: label,
      label,
      value: value || '',
      eliminable: false,
      choices: null,
      type: 'CharField'
    }));

  return {
    ...p,
    nombre: nombreStr,
    edad,
    camposExtra: [...camposBase, ...camposCustom],
    imagenes: []
  };
}

// Mensajes de vuelta del callback de OAuth de Google Calendar (ver
// google_calendar/views.py::GoogleCalendarCallbackView en el backend, que
// redirige acá con ?google_calendar=connected|error&reason=...).
function mensajeCallbackGoogleCalendar(estado, motivo) {
  if (estado === 'connected') {
    return { tipo: 'ok', texto: 'Google Calendar conectado. Los eventos van a aparecer en unos minutos.' };
  }
  if (estado === 'error') {
    if (motivo === 'access_denied') return { tipo: 'error', texto: 'Cancelaste la conexión con Google.' };
    if (motivo === 'scope_declined') return { tipo: 'error', texto: 'Para conectar Google Calendar tenés que aceptar todos los permisos que pide la pantalla de Google.' };
    if (motivo === 'session_expired') return { tipo: 'error', texto: 'Tu sesión venció durante la conexión. Volvé a intentarlo.' };
    return { tipo: 'error', texto: 'No se pudo conectar Google Calendar. Volvé a intentarlo.' };
  }
  return null;
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-izquierda">
        <img src="/LogoSoftCraft.png" alt="Logo" className="footer-logo" />
        <span className="footer-nombre">SoftCraftSolutionsSde</span>
      </div>
      <div className="footer-centro">{"© "}{new Date().getFullYear()}{" - Desarrollado por SoftCraftSolutionsSde"}</div>
      <div className="footer-derecha">
        <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="footer-privacidad">
          Política de Privacidad
        </a>
        <InstagramLink />
      </div>
    </footer>
  );
}

function App() {
  const [pacientes, setPacientes]           = useState([]);
  const [busqueda, setBusqueda]             = useState('');
  const [filtroSexo, setFiltroSexo]         = useState('');
  const [filtroArea, setFiltroArea]         = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [pacienteActivoId, setPacienteActivoId] = useState(null);
  const [vista, setVista] = useState('inicio'); // 'inicio' | 'calendario' — solo aplica cuando no hay paciente seleccionado
  const [mensajeExito, setMensajeExito]     = useState('');
  const [usuarioLogueado, setUsuarioLogueado] = useState(JSON.parse(localStorage.getItem("user") || "null"));
  const [modalGestion, setModalGestion]     = useState(false);
  const [gestionTabInicial, setGestionTabInicial] = useState('datos');
  const [gestionMensajeInicial, setGestionMensajeInicial] = useState(null);
  const [modalAdmin, setModalAdmin]         = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(false);
  const [errorEliminarPaciente, setErrorEliminarPaciente] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [modalNuevoPaciente, setModalNuevoPaciente] = useState(false);
  const [creandoPaciente, setCreandoPaciente] = useState(false);
  const [pendientesGoogleCount, setPendientesGoogleCount] = useState(0);
  const [modalBandejaGoogle, setModalBandejaGoogle] = useState(false);
  // Contador que Calendario.jsx/PanelInicio.jsx miran para refetchear turnos
  // cuando la bandeja de Google Calendar crea o cancela un Appointment
  // (link/link-new-patient/mark-as-event, y su undo) — ninguno de los dos
  // tiene forma de enterarse solo, ya que viven fuera de ModalBandejaGoogle.
  const [turnosRefreshSignal, setTurnosRefreshSignal] = useState(0);

  const user = usuarioLogueado;

  const cargarPacientes = async (opciones = {}) => {
    try {
      const { inactivos = mostrarInactivos, sexo = filtroSexo, area = filtroArea } = opciones;
      const data = await getPatients({ showInactive: inactivos, sex: sexo, area });
      const formateados = (data || []).map(formatearPaciente);
      formateados.sort((a, b) =>
        (a.last_name || '').localeCompare(b.last_name || '') ||
        (a.first_name || '').localeCompare(b.first_name || '')
      );
      setPacientes(formateados);
    } catch (error) {
      console.error("Error cargando pacientes:", error);
    }
  };

  useEffect(() => {
    if (usuarioLogueado) cargarPacientes();
  }, [usuarioLogueado]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarPendientesGoogleCount = async () => {
    try {
      const { count } = await getPendingEventsCount();
      setPendientesGoogleCount(count);
    } catch (error) {
      // Silencioso -- el contador de la campanita no debe romper el resto
      // de la app si el profesional nunca conectó Google Calendar o falla
      // este fetch puntual.
    }
  };

  // Campanita de notificaciones (etapa 3b): pollea el contador de
  // pendientes cada 60s mientras la pestaña está visible. Se pausa (no se
  // reinicia el intervalo, solo se salta el fetch) cuando la pestaña pasa a
  // segundo plano, y se refresca de una al volver a foco para no esperar
  // hasta el próximo tick.
  useEffect(() => {
    if (!usuarioLogueado) return;
    cargarPendientesGoogleCount();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') cargarPendientesGoogleCount();
    };
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') cargarPendientesGoogleCount();
    }, 60000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [usuarioLogueado]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPendientesGoogleResueltos = (cantidad) => {
    setPendientesGoogleCount(prev => Math.max(0, prev - cantidad));
  };

  const onTurnosActualizados = () => setTurnosRefreshSignal(n => n + 1);

  // Vuelta del callback de OAuth de Google Calendar: el backend redirige acá
  // con ?google_calendar=connected|error&reason=... (nunca hay router propio,
  // así que se lee del query string una sola vez al montar). Abre la gestión
  // de usuario directo en la pestaña de Google Calendar con el mensaje
  // correspondiente, y limpia la URL para que un refresh no lo vuelva a mostrar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const estado = params.get('google_calendar');
    if (!estado) return;
    const mensaje = mensajeCallbackGoogleCalendar(estado, params.get('reason'));
    setGestionMensajeInicial(mensaje);
    setGestionTabInicial('gcal');
    setModalGestion(true);
    const url = new URL(window.location.href);
    url.searchParams.delete('google_calendar');
    url.searchParams.delete('reason');
    window.history.replaceState({}, '', url.toString());
  }, []);

  if (!usuarioLogueado) return <Login onLogin={(userData) => setUsuarioLogueado(userData)} />;

  const mostrarMensaje = (txt) => {
    setMensajeExito(txt);
    setTimeout(() => setMensajeExito(''), 2500);
  };

  const handleFiltroSexo = (sexo) => {
    setFiltroSexo(sexo);
    cargarPacientes({ sexo });
  };

  const handleFiltroArea = (area) => {
    setFiltroArea(area);
    cargarPacientes({ area });
  };

  const handleToggleInactivos = () => {
    const nuevo = !mostrarInactivos;
    setMostrarInactivos(nuevo);
    cargarPacientes({ inactivos: nuevo });
  };

  const pacientesFiltrados = (pacientes || []).filter((p) => {
    const t = (busqueda || '').toLowerCase();
    const nombreStr = String(p.nombre || '');
    const ciudadStr = String(p.camposExtra?.find(c => c.id === 'city')?.value || '');
    return nombreStr.toLowerCase().includes(t) || ciudadStr.toLowerCase().includes(t);
  });

  const pacienteActivo = (pacientes || []).find(p => p.id === pacienteActivoId) || null;

  const abrirModalNuevoPaciente = () => {
    setModalNuevoPaciente(true);
  };

  const confirmarNuevoPaciente = async (nombre, apellido) => {
    setCreandoPaciente(true);
    try {
      const data = await createPatient(nombre.trim() || 'Nuevo', apellido.trim() || 'Paciente');
      const nuevo = formatearPaciente(data);
      setPacientes(prev => [...(prev || []), nuevo]);
      setPacienteActivoId(nuevo.id);
      setModalNuevoPaciente(false);
      mostrarMensaje('Paciente creado con éxito');
    } catch (err) {
      console.error("Error creando paciente:", err);
    } finally {
      setCreandoPaciente(false);
    }
  };

  const pedirConfirmacionEliminar = () => {
    if (!pacienteActivoId) return;
    setErrorEliminarPaciente(null);
    setConfirmEliminar(true);
  };

  const confirmarEliminarPaciente = async (password) => {
    try {
      await deletePatient(pacienteActivoId, password);
      setConfirmEliminar(false);
      setErrorEliminarPaciente(null);
      setPacientes(prev => (prev || []).filter(p => p.id !== pacienteActivoId));
      setPacienteActivoId(null);
      mostrarMensaje('Paciente eliminado con éxito');
    } catch (err) {
      console.error("Error eliminando paciente:", err);
      setErrorEliminarPaciente(err.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    setUsuarioLogueado(null);
  };

  const guardarFicha = (nombre, camposExtra) => {
    const nombreStr = typeof nombre === 'string' ? nombre : `${nombre.first_name || ''} ${nombre.last_name || ''}`.trim();
    setPacientes(prev => (prev || []).map(p =>
      p.id === pacienteActivoId ? { ...p, nombre: nombreStr, camposExtra: camposExtra || [] } : p
    ));
    mostrarMensaje('Ficha guardada con éxito');
  };

  const actualizarPaciente = (pacienteActualizado) => {
    const edad = calcularEdad(pacienteActualizado.birth_date);
    setPacientes(prev => prev.map(p =>
      p.id === pacienteActualizado.id ? { ...pacienteActualizado, edad } : p
    ));
  };

  const onPacienteInactivado = async () => {
    // Los turnos del paciente desaparecen automáticamente del calendario
    // porque el backend filtra por patient__is_active=True.
    // Solo recargamos la lista y volvemos al calendario.
    await cargarPacientes();
    setPacienteActivoId(null);
  };

  return (
    <div className="app-container">
      <Navbar
        onAgregarPaciente={abrirModalNuevoPaciente}
        onEliminarPaciente={pedirConfirmacionEliminar}
        onToggleSidebar={() => setSidebarVisible(v => !v)}
        pacienteActivoId={pacienteActivoId}
        mensajeExito={mensajeExito}
        onLogout={handleLogout}
        onVerInicio={() => { setVista('inicio'); setPacienteActivoId(null); }}
        onVerCalendario={() => { setVista('calendario'); setPacienteActivoId(null); }}
        onGestionUsuario={() => { setGestionTabInicial('datos'); setGestionMensajeInicial(null); setModalGestion(true); }}
        onAdminUsuarios={() => setModalAdmin(true)}
        pendientesGoogleCount={pendientesGoogleCount}
        onAbrirBandejaGoogle={() => setModalBandejaGoogle(true)}
      />
      <TrialBanner usuario={usuarioLogueado} />
      {sidebarVisible && (
        <div className="sidebar-overlay" onClick={() => setSidebarVisible(false)} />
      )}
      <div className="app-body">
        <aside className={`sidebar ${sidebarVisible ? 'sidebar-mobile-visible' : ''}`}>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar paciente..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <span className="icon-lupa">🔍</span>
          </div>

          {/* Filtros */}
          <div className="sidebar-filtros">
            <select
              className="filtro-sexo"
              value={filtroSexo}
              onChange={(e) => handleFiltroSexo(e.target.value)}
            >
              <option value="">Todos los sexos</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="O">Otro</option>
            </select>
            <button
              className={`filtro-inactivos ${mostrarInactivos ? 'filtro-inactivos-activo' : ''}`}
              onClick={handleToggleInactivos}
              title={mostrarInactivos ? 'Ocultar pacientes inactivos' : 'Mostrar pacientes inactivos — hacé clic en uno para reactivarlo desde su ficha'}
            >
              {mostrarInactivos ? 'Ocultar inactivos' : 'Ver inactivos'}
            </button>
          </div>
          {user?.profession === 'physiotherapist' && (
            <div className="sidebar-filtros">
              <select
                className="filtro-sexo"
                value={filtroArea}
                onChange={(e) => handleFiltroArea(e.target.value)}
              >
                <option value="">Todas las áreas</option>
                <option value="motor">Área Motora</option>
                <option value="respiratory">Área Respiratoria</option>
                <option value="both">Ambas</option>
              </select>
            </div>
          )}

          <div className="tabs">
            {pacientesFiltrados.length > 0 ? (
              pacientesFiltrados.map(p => (
                <TarjetaPaciente
                  key={p.id}
                  paciente={p}
                  activo={p.id === pacienteActivoId}
                  onSeleccionar={(id) => { setPacienteActivoId(id); setSidebarVisible(false); }}
                />
              ))
            ) : (
              <div className="sin-resultados">No se encontraron pacientes.</div>
            )}
          </div>
        </aside>

        <main className="main-content">
          {pacienteActivo !== null ? (
            <FichaPaciente
              key={pacienteActivo.id}
              paciente={pacienteActivo}
              onGuardar={guardarFicha}
              onActualizarPaciente={actualizarPaciente}
              onPacienteInactivado={onPacienteInactivado}
            />
          ) : vista === 'calendario' ? (
            <Calendario
              pacientes={pacientes}
              onVerFicha={(patientId) => { setPacienteActivoId(patientId); setSidebarVisible(false); }}
              turnosRefreshSignal={turnosRefreshSignal}
              onPacienteActualizado={actualizarPaciente}
            />
          ) : (
            <PanelInicio
              pacientes={pacientes}
              onVerFicha={(patientId) => { setPacienteActivoId(patientId); setSidebarVisible(false); }}
              onVerCalendario={() => setVista('calendario')}
              onAgregarPaciente={abrirModalNuevoPaciente}
              onToggleSidebar={() => setSidebarVisible(v => !v)}
              turnosRefreshSignal={turnosRefreshSignal}
              onPacienteActualizado={actualizarPaciente}
            />
          )}
        </main>
      </div>

      {modalNuevoPaciente && (
        <ModalNuevoPaciente
          guardando={creandoPaciente}
          onConfirmar={confirmarNuevoPaciente}
          onCerrar={() => setModalNuevoPaciente(false)}
        />
      )}

      {modalBandejaGoogle && (
        <ModalBandejaGoogle
          pacientes={pacientes}
          onCerrar={() => setModalBandejaGoogle(false)}
          onResueltos={onPendientesGoogleResueltos}
          onPacientesActualizados={cargarPacientes}
          onTurnosActualizados={onTurnosActualizados}
        />
      )}

      {confirmEliminar && (
        <ModalConfirmacion
          mensaje={`¿Eliminar al paciente "${pacienteActivo?.nombre}"?`}
          textoAlerta="Esta acción borra permanentemente el historial clínico del paciente y no se puede deshacer. Si el paciente dejó el tratamiento pero podría volver, usá 'Marcar como inactivo' en su ficha en lugar de esto."
          textoConfirmar="Eliminar permanentemente"
          onConfirmar={confirmarEliminarPaciente}
          onCancelar={() => { setConfirmEliminar(false); setErrorEliminarPaciente(null); }}
          pedirPassword
          errorExterno={errorEliminarPaciente}
        />
      )}

      {modalGestion && (
        <ModalGestionUsuario
          tabInicial={gestionTabInicial}
          mensajeInicial={gestionMensajeInicial}
          onCerrar={() => setModalGestion(false)}
          onUsuarioActualizado={(nuevoUser) => {
            localStorage.setItem('user', JSON.stringify(nuevoUser));
            setUsuarioLogueado(nuevoUser);
          }}
        />
      )}
      {modalAdmin && (
        <ModalAdminUsuarios onCerrar={() => setModalAdmin(false)} />
      )}
      <Footer />
    </div>
  );
}

export default App;
